import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { styledSlots, resolveStyledSlots } from "./styledSlots";
export const SquareButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "#1c355e",
    // unnecessarily overrides theme colors when doing branding
    borderRadius: 3,
    border: 0,
    color: "#fff",
    height: 54,
    minWidth: 54,
    width: 54
  }
}, theme)));
export const SquareGreenButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "#76bc21",
    // unnecessarily overrides theme colors when doing branding
    borderRadius: 3,
    border: 0,
    color: "#fff",
    height: 54,
    minWidth: 54,
    width: 54
  }
}, theme)));
export const SquareButtonSmall = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "#1c355e",
    // unnecessarily overrides theme colors when doing branding
    borderRadius: 3,
    border: 0,
    color: "#fff",
    minWidth: 54,
    height: 40,
    width: 54
  }
}, theme)));
export const SelectionButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    color: "#1c355e",
    // secondary theme color applied in all cases when doing branding
    borderRadius: 0,
    width: 300,
    height: 50
  }
}, theme)));
export const ExitButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "#d1495b",
    borderRadius: 3,
    color: "#fff",
    height: 36,
    minWidth: 36,
    width: 36,
    "&:hover": {
      backgroundColor: "#b82f41"
    }
  }
}, theme)));
export const GreenButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "#76bc21",
    boxShadow: "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#619b1b"
    },
    height: 50
  }
}, theme)));
export const BlueButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "#1c355e",
    boxShadow: "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1c355e"
    },
    height: 50
  }
}, theme)));

/* TYPOGRAPHY */
export const BlueTextTypography = styled(Typography)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    color: "#00798c",
    fontWeight: 600
  }
}, theme)));
export const RedTextTypography = styled(Typography)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    fontSize: 12,
    color: "#d1495b",
    fontWeight: 600,
    fontStyle: "italic",
    textAlign: "center"
  }
}, theme)));

/* RADIO, CHECKBOX, SWITCH */
export const GreenRadio = styled(props => <Radio color="default" {...props} />)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    color: "#76bc21",
    "&$checked": {
      color: "#76bc21"
    }
  },
  checked: {}
}, theme)));
export const GreenCheckbox = styled(props => <Checkbox color="default" {...props} />)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    color: "#76bc21",
    "&$checked": {
      color: "#76bc21"
    }
  },
  checked: {}
}, theme)));
export const GreenSwitch = styled(Switch)(({
  theme
}) => styledSlots(resolveStyledSlots({
  switchBase: {
    color: "#fff",
    "&$checked": {
      color: "#76bc21"
    },
    "&$checked + $track": {
      backgroundColor: "#76bc21"
    }
  },
  checked: {},
  track: {}
}, theme)));

/* TABS */
export const CustomStyledTabs = styled(Tabs)(({
  theme
}) => styledSlots(resolveStyledSlots({
  indicator: {
    backgroundColor: "#76bc21"
  }
}, theme)));
export const CustomStyledTab = styled(props => <Tab {...props} />)(({
  theme
}) => styledSlots(resolveStyledSlots(theme => ({
  root: {
    textTransform: "none",
    //This removes the all caps default state
    minHeight: 90,
    borderBottom: "1px solid #c1c1c1",
    fontWeight: theme.typography.fontWeightRegular,
    "&$selected": {
      color: "#76bc21",
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  selected: {}
}), theme)));
export const CustomStyledTabsThin = styled(Tabs)(({
  theme
}) => styledSlots(resolveStyledSlots({
  indicator: {
    backgroundColor: "#1c355e"
  }
}, theme)));
export const CustomStyledTabThin = styled(props => <Tab {...props} />)(({
  theme
}) => styledSlots(resolveStyledSlots(theme => ({
  root: {
    textTransform: "none",
    //This removes the all caps default state
    minHeight: 45,
    borderBottom: "1px solid #c1c1c1",
    backgroundColor: "#cfe8fd",
    fontWeight: theme.typography.fontWeightRegular,
    "&$selected": {
      color: "#1c355e",
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  selected: {}
}), theme)));
export const TabPanel = props => {
  const {
    children,
    value,
    index,
    ...other
  } = props;
  return <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>;
};
export const a11yProps = index => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`
  };
};
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};

/* ACCORDION */
export const CustomAccordion = styled(Accordion)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0
    },
    "&:before": {
      display: "none"
    },
    "&$expanded": {
      margin: "auto"
    },
    "&$expanded:hover": {
      boxShadow: "0 0 0 rgba(0,0,0,0.3)",
      zIndex: 0
    },
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      zIndex: 10
    }
  },
  expanded: {},
  disabled: {
    "&:hover": {
      boxShadow: "none"
    }
  }
}, theme)));
export const CustomAccordionSummary = styled(AccordionSummary)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    height: 45,
    "&$expanded": {
      height: 45
    }
  },
  content: {
    "&$expanded": {
      margin: "12px 0"
    }
  },
  expanded: {}
}, theme)));
export const CustomAccordionDetails = styled(AccordionDetails)(({
  theme
}) => styledSlots(resolveStyledSlots(theme => ({
  root: {
    padding: theme.spacing(2)
  }
}), theme)));
export const CustomHomeButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    background: "#1c355e",
    borderRadius: 5,
    padding: "8px 16px",
    margin: 12,
    display: "inline-block",
    transform: "translate(0%, 0%)",
    overflow: "hidden",
    color: "#fff",
    fontSize: "0.875rem",
    letterSpacing: "0.02857em",
    textAlign: "center",
    textTransform: "uppercase",
    textDecoration: "none",
    boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
    transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&:hover": {
      background: "#132541",
      boxShadow: "0px 3px 10px -2px rgba(0,0,0,0.2), 0px 2px 8px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)"
    }
  }
}, theme)));
export const CustomAnimatedButton = styled(Button)(({
  theme
}) => styledSlots(resolveStyledSlots({
  root: {
    background: "#1c355e",
    borderRadius: 5,
    padding: "8px 16px",
    margin: 12,
    display: "inline-block",
    transform: "translate(0%, 0%)",
    overflow: "hidden",
    color: "#fff",
    fontSize: "0.875rem",
    letterSpacing: "0.02857em",
    textAlign: "center",
    textTransform: "uppercase",
    textDecoration: "none",
    boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
    transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "#ad8585",
      opacity: 0,
      transition: ".2s opacity ease-in-out"
    },
    "&:hover": {
      background: "#132541",
      boxShadow: "0px 3px 10px -2px rgba(0,0,0,0.2), 0px 2px 8px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)"
    },
    "& span": {
      position: "absolute"
    },
    "& span:nth-child(1)": {
      top: 0,
      left: 0,
      width: "100%",
      height: 2,
      background: "linear-gradient(to left, rgba(46, 86, 153, 0), #86a5db)",
      animation: "$animateTop 2s linear infinite"
    },
    "& span:nth-child(2)": {
      top: 0,
      right: 0,
      height: "100%",
      width: 2,
      background: "linear-gradient(to top, rgba(46, 86, 153, 0), #86a5db)",
      animation: "$animateRight 2s linear -1s infinite"
    },
    "& span:nth-child(3)": {
      bottom: 0,
      left: 0,
      width: "100%",
      height: 2,
      background: "linear-gradient(to right, rgba(46, 86, 153, 0), #86a5db)",
      animation: "$animateBottom 2s linear infinite"
    },
    "& span:nth-child(4)": {
      top: 0,
      left: 0,
      height: "100%",
      width: 2,
      background: "linear-gradient(to bottom, rgba(46, 86, 153, 0), #86a5db)",
      animation: "$animateLeft 2s linear -1s infinite"
    }
  },
  [`@keyframes animateTop`]: {
    from: {
      transform: "translateX(100%)"
    },
    to: {
      transform: "translateX(-100%)"
    }
  },
  [`@keyframes animateRight`]: {
    from: {
      transform: "translateY(100%)"
    },
    to: {
      transform: "translateY(-100%)"
    }
  },
  [`@keyframes animateBottom`]: {
    from: {
      transform: "translateX(-100%)"
    },
    to: {
      transform: "translateX(100%)"
    }
  },
  [`@keyframes animateLeft`]: {
    from: {
      transform: "translateY(-100%)"
    },
    to: {
      transform: "translateY(100%)"
    }
  }
}, theme)));
